import React, { useEffect, useRef, useState } from 'react';
import { loadAudioPeaks, slicePeaks } from '../../lib/audioWaveform';

type Props = {
  source: Blob | string | null | undefined;
  trimStart?: number;
  trimEnd?: number;
  originalDuration?: number;
  muted?: boolean;
  color?: string;
};

export default function AudioWaveform({
  source,
  trimStart = 0,
  trimEnd = 0,
  originalDuration = 0,
  muted = false,
  color = 'rgba(134,239,172,0.92)',
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [peaks, setPeaks] = useState<Float32Array | null>(null);

  useEffect(() => {
    if (!source) {
      setPeaks(null);
      return;
    }
    let cancelled = false;
    void loadAudioPeaks(source).then((next) => {
      if (!cancelled) setPeaks(next);
    });
    return () => { cancelled = true; };
  }, [source]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (width < 2 || height < 2) return;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const visible = peaks
        ? slicePeaks(peaks, trimStart, trimEnd, originalDuration)
        : null;
      const bars = Math.max(8, Math.floor(width / 3));
      const gap = 1;
      const barW = Math.max(1, width / bars - gap);
      const mid = height / 2;
      ctx.fillStyle = muted ? 'rgba(255,255,255,0.28)' : color;

      for (let i = 0; i < bars; i++) {
        let amp = 0.08;
        if (visible && visible.length) {
          const start = Math.floor((i / bars) * visible.length);
          const end = Math.max(start + 1, Math.floor(((i + 1) / bars) * visible.length));
          let max = 0;
          for (let j = start; j < end && j < visible.length; j++) {
            if (visible[j] > max) max = visible[j];
          }
          amp = Math.max(0.06, max);
        }
        const barH = amp * (height * 0.88);
        ctx.fillRect(i * (barW + gap), mid - barH / 2, barW, barH);
      }
    };

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [peaks, trimStart, trimEnd, originalDuration, muted, color]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        display: 'block', pointerEvents: 'none',
      }}
    />
  );
}

export function waveformSource(
  blob?: Blob | null,
  blobUrl?: string | null,
  remoteUrl?: string | null,
): Blob | string | null {
  if (blob && blob.size > 32) return blob;
  if (blobUrl && (blobUrl.startsWith('blob:') || blobUrl.startsWith('http'))) return blobUrl;
  if (remoteUrl && /^https?:/i.test(remoteUrl)) return remoteUrl;
  return null;
}
